import {GameMasterApi} from '@/api/backendService.js';

/**
 * GameMasterFight Service
 * Handles fetching and managing fight information for the GameMaster
 * Provides functionality to list, filter, and stop fights
 */
export class GameMasterFightService {
    constructor() {
        this.fights = new Map(); // Map of fight IDs to fight data
        this.isLoading = false;
        this.lastLoaded = null;
        this.eventListeners = new Map();
        this.currentFilters = {
            positionId: null,
            characterId: null,
            open: true, // Default to showing only active fights
            duel: undefined,
            sealed: undefined,
            training: undefined
        };
        this.wsBus = null;
        this.wsHandlers = null;
    }

    /**
     * Subscribe to the shared WebSocket event bus for side/participant
     * changes, and relay them as this service's own events
     * (`fightSideChanged`, `fightParticipantSideChanged`) rather than raw
     * bus events — components go through the service, never the bus
     * directly. Safe to call again with a different bus; the previous
     * subscription is torn down first.
     */
    subscribe(bus) {
        this.unsubscribe();
        if (!bus) {
            return;
        }
        this.wsBus = bus;
        this.wsHandlers = {
            'fight::fight_side_created': (data) => this.emit('fightSideChanged', data),
            'fight::fight_side_updated': (data) => this.emit('fightSideChanged', data),
            'fight::fight_participant_side_changed': (data) => this.emit('fightParticipantSideChanged', data),
        };
        Object.entries(this.wsHandlers).forEach(([event, handler]) => {
            this.wsBus.on(event, handler);
        });
    }

    /**
     * Unsubscribe from the WebSocket event bus. Use on unmount.
     */
    unsubscribe() {
        if (this.wsBus && this.wsHandlers) {
            Object.entries(this.wsHandlers).forEach(([event, handler]) => {
                this.wsBus.off(event, handler);
            });
        }
        this.wsBus = null;
        this.wsHandlers = null;
    }

    /**
     * Initialize the service
     */
    async initialize() {
        try {
            console.log('Initializing GameMasterFightService...');
            return true;
        } catch (error) {
            console.error('Failed to initialize GameMasterFightService:', error);
            throw error;
        }
    }

    /**
     * Load all fights with optional filters
     * @param {Object} filters - Filter options
     * @param {string} filters.positionId - Filter by position ID
     * @param {string} filters.characterId - Filter by character ID
     * @param {boolean} filters.open - Filter by open status
     * @param {boolean} forceRefresh - Whether to force a refresh from the server
     */
    async loadFights(filters = {}, forceRefresh = false) {
        // Merge with current filters
        this.currentFilters = {...this.currentFilters, ...filters};

        // Use cached data if not forcing refresh
        if (!forceRefresh && this.fights.size > 0 && this.lastLoaded) {
            const cacheAge = Date.now() - this.lastLoaded.getTime();
            if (cacheAge < 30000) { // 30 seconds cache
                console.log('Using cached fights data');
                return this.getFightsArray();
            }
        }

        if (this.isLoading) {
            console.log('Fights already loading, waiting for completion...');
            return null;
        }

        try {
            this.isLoading = true;
            this.emit('loadingStarted');

            console.log('Loading fights from server with filters:', this.currentFilters);

            /* (characterId, duel, open, positionId, sealed, training) —
               positional, and the generated order is not the obvious one. */
            const response = await GameMasterApi.gamemasterFightsList(
                undefined,
                this.currentFilters.duel,
                true,
                undefined,
                this.currentFilters.sealed,
                this.currentFilters.training
            );

            const payload = Array.isArray(response?.data) ? response.data : [];

            this.fights.clear();

            this.applyLocalFilters(payload).forEach(fight => {
                this.fights.set(fight.id, fight);
            });

            this.lastLoaded = new Date();

            console.log(`Loaded ${this.fights.size} fights`);
            this.emit('fightsLoaded', this.getFightsArray());

            return this.getFightsArray();
        } catch (error) {
            if (error?.response?.status === 404) {
                this.fights.clear();
                this.lastLoaded = new Date();
                this.emit('fightsLoaded', []);
                return [];
            }

            console.error('Failed to load fights:', error);
            this.emit('loadingFailed', error);
            throw error;
        } finally {
            this.isLoading = false;
        }
    }

    /**
     * Apply position/character filters locally
     * @param {Array} fights - Fights returned by the API
     */
    applyLocalFilters(fights) {
        const {positionId, characterId} = this.currentFilters;

        return fights.filter(fight => {
            if (positionId && fight.position !== positionId) {
                return false;
            }
            if (characterId && !this.involvesCharacter(fight, characterId)) {
                return false;
            }
            return true;
        });
    }

    /**
     * Check whether a character participates in a fight
     * @param {Object} fight - Fight payload
     * @param {string} characterId - Character ID
     */
    involvesCharacter(fight, characterId) {
        if (fight.attacker?.id === characterId || fight.defender?.id === characterId) {
            return true;
        }
        return (fight.joined || []).some(participant => participant.id === characterId);
    }

    /**
     * Get fights as array
     */
    getFightsArray() {
        return Array.from(this.fights.values());
    }

    /**
     * Get fight by ID
     * @param {number} fightId - The ID of the fight to get
     */
    async getFight(fightId) {
        if (this.fights.has(fightId)) {
            return this.fights.get(fightId);
        }

        // Load single fight from server
        try {
            console.log(`Loading fight ${fightId} from server...`);
            const response = await GameMasterApi.gamemasterFightsRetrieve(fightId);

            if (response.data) {
                this.fights.set(fightId, response.data);
                return response.data;
            }
        } catch (error) {
            console.error(`Failed to load fight ${fightId}:`, error);
            throw error;
        }

        return null;
    }

    /**
     * End one fight's current phase now, without waiting for its deadline.
     *
     * The GM's step button while the fight worker loop is off
     * (`FIGHT_WORKER_ENABLED`). Everyone who has not ended their turn has it
     * ended for them, exactly as the worker would on expiry.
     *
     * @param {string} fightId
     * @returns {Promise<{fights: object, round: number, phase: string, open: boolean}>}
     */
    async advanceFightPhase(fightId) {
        const response = await GameMasterApi.gamemasterFightsAdvancePhaseCreate(fightId);
        return response.data;
    }

    /**
     * Run one pass of the fight worker by hand: expire overdue deadlines, end
     * stragglers' turns, advance fully-acked fights, close on inactivity.
     *
     * Use `advanceFightPhase` instead for a specific fight that is not yet
     * owing anything — a tick cannot see it.
     *
     * @returns {Promise<{fights: object}>}
     */
    async tickFightClock() {
        const response = await GameMasterApi.gamemasterFightsTickCreate();
        return response.data;
    }

    /**
     * Stop a single fight
     * @param {number} fightId - The ID of the fight to stop
     * @param {string} reason - Reason for stopping the fight
     */
    async stopFight(fightId, reason = 'Stopped by Game Master') {
        try {
            console.log(`Stopping fight ${fightId}...`);
            this.emit('stoppingFight', fightId);

            const response = await GameMasterApi.gamemasterFightsStopFightCreate(
                fightId,
                {reason}
            );

            if (response.data && response.data.success) {
                // Remove from local cache or mark as closed
                const fight = this.fights.get(fightId);
                if (fight) {
                    fight.open = false;
                    this.fights.set(fightId, fight);
                }

                console.log(`Fight ${fightId} stopped successfully`);
                this.emit('fightStopped', {fightId, ...response.data});

                // Refresh fights list
                await this.loadFights({}, true);

                return response.data;
            }

            throw new Error(response.data?.message || 'Failed to stop fight');
        } catch (error) {
            console.error(`Failed to stop fight ${fightId}:`, error);
            this.emit('stopFightFailed', {fightId, error});
            throw error;
        }
    }

    /**
     * Stop all fights (optionally filtered)
     * @param {Object} filters - Filter options
     * @param {number} filters.positionId - Only stop fights at this position
     * @param {number} filters.characterId - Only stop fights involving this character
     * @param {string} reason - Reason for stopping the fights
     */
    async stopAllFights(filters = {}, reason = 'Mass stopped by Game Master') {
        try {
            console.log('Stopping all fights with filters:', filters);
            this.emit('stoppingAllFights', filters);

            const requestBody = {
                ...filters,
                reason
            };

            const response = await GameMasterApi.gamemasterFightsStopAllFightsCreate(requestBody);

            if (response.data && response.data.success) {
                // Update local cache - mark stopped fights as closed
                response.data.stopped_fight_ids?.forEach(fightId => {
                    const fight = this.fights.get(fightId);
                    if (fight) {
                        fight.open = false;
                        this.fights.set(fightId, fight);
                    }
                });

                console.log(`Stopped ${response.data.stopped_count} fights`);
                this.emit('allFightsStopped', response.data);

                // Refresh fights list
                await this.loadFights({}, true);

                return response.data;
            }

            throw new Error(response.data?.message || 'Failed to stop fights');
        } catch (error) {
            console.error('Failed to stop all fights:', error);
            this.emit('stopAllFightsFailed', error);
            throw error;
        }
    }

    /**
     * List a fight's sides — the donut/side-rail shape, server-summarized.
     * @param {string} fightId - The fight to list sides for
     */
    async listSides(fightId) {
        const response = await GameMasterApi.gamemasterFightsSidesList(fightId);
        return response.data || [];
    }

    /**
     * List a fight's participants and the side each currently sits on.
     * @param {string} fightId - The fight to list participants for
     */
    async listParticipants(fightId) {
        const response = await GameMasterApi.gamemasterFightsParticipantsList(fightId);
        return response.data || [];
    }

    /**
     * Create a new side on a fight. Refused past `MAX_FIGHT_SIDES` (400).
     * @param {string} fightId - The fight to create the side on
     * @param {string} name - The side's name
     */
    async createSide(fightId, name) {
        const response = await GameMasterApi.gamemasterFightsSidesCreate(fightId, {name});
        this.emit('sideCreated', {fightId, side: response.data});
        return response.data;
    }

    /**
     * Rename a side. Never refused — a name collision gets a numeric suffix
     * server-side rather than a 400.
     * @param {string} fightId - The fight the side belongs to
     * @param {string} sideId - The side to rename
     * @param {string} name - The new name
     */
    async renameSide(fightId, sideId, name) {
        const response = await GameMasterApi.gamemasterFightsSidesPartialUpdate(fightId, sideId, {name});
        this.emit('sideUpdated', {fightId, side: response.data});
        return response.data;
    }

    /**
     * Delete a side. Refused while it still has members (400) — an emptied
     * side is left in place rather than auto-deleted.
     * @param {string} fightId - The fight the side belongs to
     * @param {string} sideId - The side to delete
     */
    async deleteSide(fightId, sideId) {
        await GameMasterApi.gamemasterFightsSidesDestroy(fightId, sideId);
        this.emit('sideDeleted', {fightId, sideId});
    }

    /**
     * Move a participant to a different side of the same fight. Refused if
     * the target side belongs to another fight (400).
     *
     * The generated method's argument order is `(characterId, fightPk, body)`
     * — `characterId` first, unlike the sides routes above where `fightPk`
     * leads. Get this backwards and every call 404s against the wrong URL.
     * @param {string} fightId - The fight the participant belongs to
     * @param {string} characterId - The participant to move
     * @param {string} sideId - The side to move them to
     */
    async moveParticipant(fightId, characterId, sideId) {
        const response = await GameMasterApi.gamemasterFightsParticipantsPartialUpdate(
            characterId,
            fightId,
            {side_id: sideId}
        );
        this.emit('participantMoved', {fightId, characterId, sideId, participant: response.data});
        return response.data;
    }

    /**
     * Refresh fights list
     */
    async refresh() {
        return this.loadFights(this.currentFilters, true);
    }

    /**
     * Set filters and reload
     * @param {Object} filters - Filter options
     */
    async setFilters(filters) {
        this.currentFilters = {...this.currentFilters, ...filters};
        return this.loadFights(this.currentFilters, true);
    }

    /**
     * Clear filters
     */
    async clearFilters() {
        this.currentFilters = {
            positionId: null,
            characterId: null,
            open: true,
            duel: undefined,
            sealed: undefined,
            training: undefined
        };
        return this.loadFights(this.currentFilters, true);
    }

    /**
     * Get current filters
     */
    getFilters() {
        return {...this.currentFilters};
    }

    /**
     * Event system for components to listen to changes
     */
    on(event, callback) {
        if (!this.eventListeners.has(event)) {
            this.eventListeners.set(event, []);
        }
        this.eventListeners.get(event).push(callback);
    }

    /**
     * Remove event listener
     */
    off(event, callback) {
        if (this.eventListeners.has(event)) {
            const listeners = this.eventListeners.get(event);
            const index = listeners.indexOf(callback);
            if (index > -1) {
                listeners.splice(index, 1);
            }
        }
    }

    /**
     * Emit event to listeners
     */
    emit(event, data) {
        if (this.eventListeners.has(event)) {
            this.eventListeners.get(event).forEach(callback => {
                try {
                    callback(data);
                } catch (error) {
                    console.error(`Error in event listener for ${event}:`, error);
                }
            });
        }
    }

    /**
     * Reset the service to initial state
     */
    reset() {
        this.fights.clear();
        this.isLoading = false;
        this.lastLoaded = null;
        this.currentFilters = {
            positionId: null,
            characterId: null,
            open: true,
            duel: undefined,
            sealed: undefined,
            training: undefined
        };
        console.log('GameMasterFightService reset');
        this.emit('reset');
    }
}

// Export singleton instance
export const gameMasterFightService = new GameMasterFightService();
